import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { MailCheck, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { verifyEmail, resendVerification, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user, refreshUser } = useAuth();
  const [state, setState] = useState(token ? "verifying" : "idle");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (token && !ran.current) {
      ran.current = true;
      verifyEmail(token)
        .then(async (d) => { setState("ok"); setMessage(d.message); await refreshUser(); })
        .catch((e) => { setState("error"); setMessage(formatApiError(e.response?.data?.detail) || "Verification failed."); });
    }
  }, [token, refreshUser]);

  const resend = async () => {
    const target = (user && user.email) || email;
    if (!target) return;
    setBusy(true);
    try { const d = await resendVerification(target); setMessage(d.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5 py-16" data-testid="verify-email-page">
      <div className="w-full max-w-md text-center bg-card border border-border rounded-md p-8">
        {state === "verifying" && <><Loader2 className="h-10 w-10 mx-auto text-atlas-blue animate-spin mb-4" /><p>Verifying your email…</p></>}
        {state === "ok" && (
          <div data-testid="verify-success">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600 mb-4" />
            <h1 className="font-display text-2xl font-bold mb-2">Email verified</h1>
            <p className="text-muted-foreground mb-6">{message}</p>
            <Button asChild className="bg-primary text-white"><Link to="/">Start exploring</Link></Button>
          </div>
        )}
        {state === "error" && (
          <div data-testid="verify-error">
            <XCircle className="h-12 w-12 mx-auto text-red-600 mb-4" />
            <h1 className="font-display text-2xl font-bold mb-2">Verification problem</h1>
            <p className="text-muted-foreground mb-6">{message}</p>
            <Button onClick={resend} disabled={busy} data-testid="verify-resend" className="bg-primary text-white">
              {busy ? "Sending…" : "Send a new verification email"}
            </Button>
          </div>
        )}
        {state === "idle" && (
          <div data-testid="verify-pending">
            <MailCheck className="h-12 w-12 mx-auto text-atlas-blue mb-4" />
            <h1 className="font-display text-2xl font-bold mb-2">Verify your email</h1>
            <p className="text-muted-foreground mb-6">
              We sent a verification link to {user && user.email ? <span className="font-medium">{user.email}</span> : "your email"}.
              Click it to activate your account.
            </p>
            {!(user && user.email) && (
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email to resend" className="mb-3" data-testid="verify-email-input" />
            )}
            {message && <p className="text-sm text-emerald-700 mb-3">{message}</p>}
            <Button onClick={resend} disabled={busy} data-testid="verify-resend" className="bg-primary text-white w-full">
              {busy ? "Sending…" : "Resend verification email"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
